/**
 * 部屋を作る画面（issue #103・docs/01 7-9・15-1）。
 *
 * この画面の目的は「**部屋を1つ作る**」の1つだけです（docs/06 原則3）。
 * 参加者の追加・保存期間の設定は、作ったあとに開く部屋の画面で行います。
 *
 * 作れる種類は**この会社で使えるもの**をサーバから受け取ります（設定の表で種類を足せるため・
 * docs/01 7-1）。1対1はここには出ません（現場でお相手を選んで始まる部屋のため）。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import type { ChannelChoices, ManagedChannelDetail } from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { channelTypeLabel, channelTypeNote } from '../channels/labels.js';
import { ErrorBlock } from '../components/StatusBlock.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { withReason } from '../format/sentence.js';

/** 拠点の選び方。全社＝拠点を持たない部屋（会社じゅうの連絡に使う）。 */
const WHOLE_COMPANY = 'org';

export function ChannelCreatePage(): ReactNode {
  const api = useApi();
  const navigate = useNavigate();
  const { profile } = useSession();
  // 受け持ちが自拠点の方は、全社の部屋・他の拠点の部屋を作れない（サーバが断る・issue #258）。
  // 選べないものは出さず、はじめから自分の拠点を選んでおく
  const wholeCompany = profile?.grant?.scope === 'org';
  const [choices, setChoices] = useState<ChannelChoices | null>(null);
  const [choicesError, setChoicesError] = useState<string | null>(null);
  const [type, setType] = useState('');
  const [name, setName] = useState('');
  const [site, setSite] = useState<string>(WHOLE_COMPANY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<ChannelChoices>('/admin/channels/choices')
      .then((loaded) => {
        if (cancelled) return;
        setChoices(loaded);
        setChoicesError(null);
        // 最初に選んでおくのは、いちばんよく作る「グループ」（総点検 M20）
        setType(
          (current) =>
            current ||
            (loaded.types.find((option) => option.key === 'group')?.key ??
              loaded.types[0]?.key ??
              ''),
        );
        if (!wholeCompany) {
          setSite((current) =>
            current === WHOLE_COMPANY ? (loaded.sites[0]?.id ?? WHOLE_COMPANY) : current,
          );
        }
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setChoicesError(caught instanceof ApiError ? caught.message : messageForStatus(0));
      });
    return () => {
      cancelled = true;
    };
  }, [api, wholeCompany]);

  async function create(): Promise<void> {
    if (name.trim() === '') {
      setError('トークの名前を入力してください。');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<ManagedChannelDetail>('/channels', {
        body: {
          type,
          name: name.trim(),
          // 全社の部屋は拠点を持たせない（null）。拠点を選んだときはその拠点の部屋になる
          site_id: site === WHOLE_COMPANY ? null : site,
        },
      });
      // 作ったあとは、その部屋の画面へ進んでいただく（参加者と保存期間はそちらで）
      navigate(`/rooms/${created.id}?created=1`);
    } catch (caught: unknown) {
      setError(caught instanceof ApiError ? caught.message : messageForStatus(0));
      setBusy(false);
    }
  }

  return (
    <article>
      <PageHeader
        title="トークを作る"
        description="種類と名前を決めて、トークを1つ作ります。参加者の追加と保存期間の設定は、作ったあとの画面で行います。"
      />

      {choicesError ? (
        <ErrorBlock message={withReason(`選択肢を読み込めませんでした`, choicesError)} />
      ) : null}

      <FormCard
        title="新しいトーク"
        submitLabel="この内容で作る"
        submitBusyLabel="作っています…"
        busy={busy}
        onSubmit={() => void create()}
        onCancel={() => navigate('/rooms')}
        cancelLabel="やめる"
        footer={
          error ? (
            <ResultBanner
              tone="error"
              message={withReason(`トークを作れませんでした`, error)}
              nextStep="入力の内容をご確認のうえ、もう一度お試しください。"
            />
          ) : undefined
        }
      >
        <label className="ui-field" htmlFor="channel-type">
          <span>種類</span>
          <select id="channel-type" value={type} onChange={(event) => setType(event.target.value)}>
            {choices?.types.map((option) => (
              <option key={option.key} value={option.key}>
                {channelTypeLabel(option.key)}
              </option>
            ))}
          </select>
        </label>
        {channelTypeNote(type) ? <p className="admin-note">{channelTypeNote(type)}</p> : null}

        <label className="ui-field" htmlFor="channel-name">
          <span>名前</span>
          <input
            id="channel-name"
            type="text"
            value={name}
            maxLength={100}
            onChange={(event) => setName(event.target.value)}
          />
        </label>

        <label className="ui-field" htmlFor="channel-site">
          <span>どの拠点のトークか</span>
          <select id="channel-site" value={site} onChange={(event) => setSite(event.target.value)}>
            {wholeCompany ? <option value={WHOLE_COMPANY}>全社</option> : null}
            {choices?.sites.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </label>
      </FormCard>
    </article>
  );
}
